import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useOnboarding, useForwardAction } from '@/components/onboarding'
import { useProviderAuth } from '@/hooks/auth'
import { providerApiClient } from '@/lib/api'
import { onboardingKeys } from '@/lib/queryKeys'
import {
  practiceProfileStepDataSchema,
  PRACTICE_SLUG_PATTERN,
  slugFromName,
} from '@/schemas/onboarding-practice-profile'
import type { PracticeProfileStepData } from '@/types/onboarding'

const STEP_INDEX = 4

function getStoredData(stepData: Record<string, unknown>): PracticeProfileStepData | null {
  const stored = stepData[STEP_INDEX]
  if (!stored) return null
  const parsed = practiceProfileStepDataSchema.safeParse(stored)
  return parsed.success ? parsed.data : null
}

// Tiny local debounce — keeps the step self-contained so we don't introduce
// a shared util just for one call site.
function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delayMs)
    return () => window.clearTimeout(id)
  }, [value, delayMs])
  return debounced
}

// Solo providers are our initial target, so the provider's display name is a
// reasonable default for both the practice name and the patient-portal slug.
// Both fields remain editable; the slug auto-tracks the name until the user
// edits it manually (see slugTouchedRef below).
function initialPracticeData(providerName: string | undefined): PracticeProfileStepData {
  const name = (providerName ?? '').trim()
  return {
    name,
    address: { street: '', city: '', state: '', zip: '' },
    phone: '',
    slug: slugFromName(name),
  }
}

export function PracticeProfileStep() {
  const { state, setStepData, markCompleted, goToStep } = useOnboarding()
  const { provider } = useProviderAuth()

  const [defaults] = useState<PracticeProfileStepData>(() => {
    const stored = getStoredData(state.stepData)
    return stored ?? initialPracticeData(provider?.name)
  })

  // A slug is considered "touched" (user-managed) when it diverges from the
  // canonical slug derived from the current name. We start touched if the
  // hydrated data already has a manually-set slug.
  const slugTouchedRef = useRef<boolean>(
    Boolean(defaults.slug) && defaults.slug !== slugFromName(defaults.name),
  )

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isValid },
  } = useForm<PracticeProfileStepData>({
    resolver: zodResolver(practiceProfileStepDataSchema),
    mode: 'onChange',
    defaultValues: defaults,
  })

  const name = watch('name')
  const slug = watch('slug')

  // Auto-generate the slug from the practice name until the user edits the
  // slug field manually. Once they do, further name changes leave the slug
  // alone.
  useEffect(() => {
    if (slugTouchedRef.current) return
    const generated = slugFromName(name ?? '')
    if (generated !== slug) {
      setValue('slug', generated, { shouldValidate: true })
    }
  }, [name, slug, setValue])

  const slugFormatValid = PRACTICE_SLUG_PATTERN.test(slug ?? '') && (slug?.length ?? 0) >= 3
  const debouncedSlug = useDebouncedValue(slug, 250)

  const slugQuery = useQuery({
    queryKey: onboardingKeys.slugAvailability(debouncedSlug ?? ''),
    queryFn: () => providerApiClient.checkPracticeSlug(debouncedSlug ?? ''),
    enabled: slugFormatValid && debouncedSlug === slug && slug.length >= 3,
    staleTime: 30 * 1000,
  })

  const slugAvailable = slugQuery.data?.available === true
  const slugUnavailable = slugQuery.data?.available === false
  const slugChecking =
    slugFormatValid && (slugQuery.isFetching || debouncedSlug !== slug)

  const onSubmit = (data: PracticeProfileStepData) => {
    setStepData(STEP_INDEX, data satisfies PracticeProfileStepData)
    markCompleted(STEP_INDEX)
    goToStep(STEP_INDEX + 1)
  }

  useForwardAction({
    label: 'Continue',
    disabled: !isValid || !slugAvailable || slugChecking,
    onClick: handleSubmit(onSubmit),
  })

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-slate-900">Practice</h2>
        <p className="mt-1 text-sm text-slate-600">
          Tell us about your practice. Patients see this information when they book
          with you.
        </p>
      </div>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="space-y-6"
        aria-label="Practice profile form"
      >
        <div className="space-y-2">
          <Label htmlFor="practice-name">Practice name</Label>
          <Input id="practice-name" {...register('name')} />
          {errors.name && <p className="text-sm text-red-600">{errors.name.message}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="practice-slug">Patient portal URL</Label>
          <Input
            id="practice-slug"
            {...register('slug', {
              onChange: () => {
                slugTouchedRef.current = true
              },
            })}
          />
          <p className="text-xs text-slate-500">
            Your patient booking page: <span className="font-mono">determa.co/p/{slug || 'your-practice'}</span>
          </p>
          {errors.slug ? (
            <p className="text-sm text-red-600">{errors.slug.message}</p>
          ) : slugChecking ? (
            <p className="text-sm text-slate-500 inline-flex items-center gap-1">
              <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
              Checking availability…
            </p>
          ) : slugUnavailable ? (
            <p className="text-sm text-red-600">That URL is already taken.</p>
          ) : slugAvailable ? (
            <p className="text-sm text-green-700">Available</p>
          ) : null}
        </div>

        <div className="space-y-2">
          <Label htmlFor="practice-street">Street address</Label>
          <Input id="practice-street" {...register('address.street')} />
          {errors.address?.street && (
            <p className="text-sm text-red-600">{errors.address.street.message}</p>
          )}
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="space-y-2">
            <Label htmlFor="practice-city">City</Label>
            <Input id="practice-city" {...register('address.city')} />
            {errors.address?.city && (
              <p className="text-sm text-red-600">{errors.address.city.message}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="practice-state">State</Label>
            <Input id="practice-state" {...register('address.state')} />
            {errors.address?.state && (
              <p className="text-sm text-red-600">{errors.address.state.message}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="practice-zip">ZIP</Label>
            <Input id="practice-zip" {...register('address.zip')} />
            {errors.address?.zip && (
              <p className="text-sm text-red-600">{errors.address.zip.message}</p>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="practice-phone">Phone</Label>
          <Input id="practice-phone" type="tel" {...register('phone')} />
          {errors.phone && <p className="text-sm text-red-600">{errors.phone.message}</p>}
        </div>
      </form>
    </div>
  )
}
